import type { DataRefreshOutcome, DataRefreshTrigger } from './types';

const DEFAULT_BACKGROUND_STALE_MS = 60_000;
const DEFAULT_AUTO_COOLDOWN_MS = 30_000;

export interface DataRefreshLifecycleOptions {
  refresh: (trigger: DataRefreshTrigger) => Promise<DataRefreshOutcome>;
  isReady: () => boolean;
  isOnline: () => boolean;
  isVisible: () => boolean;
  now?: () => number;
  backgroundStaleMs?: number;
  autoCooldownMs?: number;
}

export interface DataRefreshLifecycle {
  handleVisibilityChange: (isVisible: boolean) => Promise<DataRefreshOutcome | null>;
  handleOnline: () => Promise<DataRefreshOutcome | null>;
  handleOffline: () => void;
  handleReadinessChange: (isReady: boolean) => void;
  dispose: () => void;
}

export const createDataRefreshLifecycle = (options: DataRefreshLifecycleOptions): DataRefreshLifecycle => {
  const now = options.now ?? Date.now;
  const backgroundStaleMs = options.backgroundStaleMs ?? DEFAULT_BACKGROUND_STALE_MS;
  const autoCooldownMs = options.autoCooldownMs ?? DEFAULT_AUTO_COOLDOWN_MS;
  let hiddenAt: number | null = options.isVisible() ? null : now();
  let lastAutoAttemptAt = Number.NEGATIVE_INFINITY;
  let hasDeferredOnlineRefresh = false;
  let isDisposed = false;

  const tryAutomaticRefresh = (trigger: Extract<DataRefreshTrigger, 'visible' | 'online'>) => {
    if (isDisposed || !options.isReady() || !options.isOnline() || !options.isVisible()) {
      return Promise.resolve(null);
    }

    const attemptAt = now();
    if (attemptAt - lastAutoAttemptAt < autoCooldownMs) {
      return Promise.resolve(null);
    }

    lastAutoAttemptAt = attemptAt;
    return options.refresh(trigger);
  };

  const handleVisibilityChange = (isVisible: boolean): Promise<DataRefreshOutcome | null> => {
    if (isDisposed) {
      return Promise.resolve(null);
    }

    if (!isVisible) {
      hiddenAt = now();
      return Promise.resolve(null);
    }

    const backgroundDuration = hiddenAt === null ? 0 : now() - hiddenAt;
    hiddenAt = null;

    if (hasDeferredOnlineRefresh) {
      hasDeferredOnlineRefresh = false;
      return tryAutomaticRefresh('online');
    }

    if (backgroundDuration < backgroundStaleMs) {
      return Promise.resolve(null);
    }

    return tryAutomaticRefresh('visible');
  };

  return {
    handleVisibilityChange,
    handleOnline: () => {
      if (isDisposed || !options.isReady()) {
        hasDeferredOnlineRefresh = false;
        return Promise.resolve(null);
      }

      if (!options.isVisible()) {
        hasDeferredOnlineRefresh = true;
        return Promise.resolve(null);
      }

      return tryAutomaticRefresh('online');
    },
    handleOffline: () => undefined,
    handleReadinessChange: (isReady) => {
      if (!isReady) {
        hasDeferredOnlineRefresh = false;
      }
    },
    dispose: () => {
      isDisposed = true;
      hasDeferredOnlineRefresh = false;
      hiddenAt = null;
    },
  };
};
